import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { BRAND } from "@/lib/constants";
import { Menu, X } from "lucide-react";

const SECTION_NAV = [
  { label: "Boliger", to: "/#boliger" },
  { label: "Services", to: "/#services" },
  { label: "Hvorfor os", to: "/#why" },
  { label: "Pris", to: "/#pricing" },
];

const PAGE_NAV = [
  { label: "Cases", to: "/cases", highlight: true },
  { label: "Firmaboliger", to: "/firmaboliger" },
  { label: "For gæster", to: "/for-gaester" },
  { label: "Om os", to: "/om-os" },
  { label: "FAQ", to: "/faq" },
];

const MOBILE_EXTRA = [
  { label: "Sådan starter du", to: "/#process" },
  { label: "Kontakt", to: "/#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const overHero = !scrolled && !open;
  const textColor = overHero ? "text-white" : "text-[#111111]";
  const subTextColor = overHero ? "text-[#D4AF37]" : "text-[#C9A227]";

  const navLinkBase = `link-underline text-sm transition-colors duration-500 ${
    overHero ? "text-white/90 hover:text-[#D4AF37]" : "text-[#222222] hover:text-[#C9A227]"
  }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-white border-b border-black/5"
          : "bg-gradient-to-b from-black/40 via-black/15 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between h-16 sm:h-20 gap-3">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-2.5 sm:gap-3 group min-w-0"
        >
          <img
            src={BRAND.logoUrl}
            alt="Gudiksen Stays"
            className="h-11 w-11 sm:h-16 sm:w-16 object-contain shrink-0 translate-y-[6px] sm:translate-y-[9px] transition-all duration-500"
          />
          <div className="flex flex-col leading-none min-w-0">
            <span
              className={`font-serif text-sm sm:text-lg tracking-[0.2em] sm:tracking-[0.25em] transition-colors duration-500 ${textColor}`}
              style={{ fontFamily: "Fraunces, serif" }}
            >
              GUDIKSEN
            </span>
            <span
              className={`text-[8px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] mt-1 transition-colors duration-500 ${subTextColor}`}
            >
              - STAYS -
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {SECTION_NAV.map((n) => (
            <HashLink
              key={n.to}
              smooth
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`${navLinkBase} whitespace-nowrap`}
            >
              {n.label}
            </HashLink>
          ))}
          {PAGE_NAV.map((n) =>
            n.hash ? (
              <HashLink
                key={n.to}
                smooth
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`${navLinkBase} whitespace-nowrap`}
              >
                {n.label}
              </HashLink>
            ) : (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={
                  n.highlight
                    ? `relative inline-flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors duration-500 ${overHero ? "text-[#D4AF37]" : "text-[#C9A227]"}`
                    : `${navLinkBase} whitespace-nowrap`
                }
              >
                {n.label}
                {n.highlight && (
                  <span className={`text-[8px] tracking-[0.25em] uppercase px-1.5 py-0.5 border ${overHero ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#C9A227]/60 text-[#C9A227]"}`}>
                    Ny
                  </span>
                )}
              </Link>
            )
          )}
        </nav>

        <div className="hidden xl:block">
          <a
            href={BRAND.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-btn"
            className="btn-gold whitespace-nowrap"
            style={
              overHero
                ? { background: "#D4AF37", borderColor: "#D4AF37", color: "#111111" }
                : undefined
            }
          >
            <span>Book møde</span>
          </a>
        </div>

        <button
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
          className={`lg:hidden shrink-0 p-2 -mr-2 transition-colors duration-500 ${textColor}`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-black/5 px-6 py-8 space-y-5"
        >
          {[...SECTION_NAV, ...MOBILE_EXTRA, ...PAGE_NAV].map((n) => {
            const isPage = !n.to.includes("#");
            const Comp = isPage ? Link : HashLink;
            const extra = isPage ? {} : { smooth: true };
            return (
              <Comp
                key={n.to + n.label}
                to={n.to}
                onClick={() => setOpen(false)}
                {...extra}
                className="block text-base text-[#222222] tracking-wide font-light"
              >
                {n.label}
              </Comp>
            );
          })}
          <a
            href={BRAND.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="btn-gold w-full justify-center mt-2"
            data-testid="mobile-book-btn"
            style={{ background: "#D4AF37", borderColor: "#D4AF37", color: "#111111" }}
          >
            <span>Book en uforpligtende samtale</span>
          </a>
        </div>
      )}
    </header>
  );
}
